import { AxiosInstance } from 'axios';
import i18n from '../../../client/src/i18n/i18n';
import { NotificationType } from '../notification/notification.types';
import { generateNotification } from '../notification/notification.util';
import { OfferedTask } from '../offer/Offer.types';
import {
  DocumentBucket,
  FileDeleteRequest,
  FileRequest,
  FileUploadConfig,
  MultiFileRequest,
  SchambeckDoc,
} from './Files.types';

/**
 * API method to get a List of all SchambeckDocs for files in a folder
 * @param axios instance of axios
 * @param request FileRequest containing of the folder structure and the bucket
 * @returns Array of all found SchmabeckDocs
 */
export const getDocumentListForFolder = async (
  axios: AxiosInstance,
  request: FileRequest,
): Promise<SchambeckDoc[]> => {
  return axios
    .post('/document/folder', request)
    .then((res) => res.data)
    .catch((exc) => {
      console.error('Error while loading files', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.getDocumentList'),
      });
      return [];
    });
};
/**
 * API method to get a List of all SchambeckDocs for files in a folder
 * @param axios instance of axios
 * @param request FileRequest containing of the folder structure and the bucket
 * @returns Array of all found SchmabeckDocs
 */
export const getDocumentListForFolders = async (
  axios: AxiosInstance,
  request: MultiFileRequest,
): Promise<SchambeckDoc[]> => {
  return axios
    .post('/document/folders', request)
    .then((res) => res.data)
    .catch((exc) => {
      console.error('Error while loading files', exc);
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.getDocumentList'),
      });
      return [];
    });
};

/**
 * API method to delete a schambeckDoc
 * @param axios instance of axios
 * @param id of the schambeckDoc to delete
 * @returns boolean depending on success
 */
export const deleteSchambeckDoc = async (axios: AxiosInstance, id: string): Promise<boolean> => {
  return axios
    .post('/document/delete', id, {
      headers: { 'Content-Type': 'text/plain' },
    })
    .then((res) => res.status === 200)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.deleteSchambeckDoc'),
      });
      console.error('Error while deleting schambeckDoc', exc);
      return false;
    });
};

/**
 * API Method to save a new File to the minio ObjectStorage
 * @param axios instance of axios
 * @param config to save the file containing attributes like fileName, bucket etc.
 * @param fileData the file to upload
 * @returns SchambeckDoc of the saved file or undefined on error
 */
export const saveNewFile = async (
  axios: AxiosInstance,
  config: FileUploadConfig,
  fileData: File,
): Promise<SchambeckDoc | undefined> => {
  const uploadFormdata: FormData = new FormData();
  uploadFormdata.append('config', new Blob([JSON.stringify(config)], { type: 'application/json' }));
  uploadFormdata.append('fileData', fileData);

  return axios
    .post('/storage/new', uploadFormdata, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => res.data)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.saveNewFile'),
      });
      console.error('Error while saving File', exc);
      return undefined;
    });
};

/**
 * API Endpoint to import articles from an xlsx
 * @param axios instance of axios
 * @param fileData the uploaded file
 * @returns  Promise containing all created tasks
 */
export const importArticlesFromXlsx = async (
  axios: AxiosInstance,
  fileData: File,
): Promise<OfferedTask[]> => {
  const uploadFormdata: FormData = new FormData();
  uploadFormdata.append('fileData', fileData);

  return axios
    .post('/import/articles', uploadFormdata, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => res.data)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.importFile'),
      });
      console.error('Error while importing articles', exc);
      return [];
    });
};

/**
 * API Endpoint to import an actual order
 * @param axios instance of axios
 * @param fileData the provided xlsx file containing the data
 * @returns Promise containing all ordered positions as offeredTsasks
 */
export const importOrderFromXlsx = async (
  axios: AxiosInstance,
  fileData: File,
): Promise<OfferedTask[]> => {
  const uploadFormdata: FormData = new FormData();
  uploadFormdata.append('fileData', fileData);
  return axios
    .post('/import/order', uploadFormdata, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    .then((res) => res.data)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.importFile'),
      });
      console.error('Error while importing articles', exc);
      return [];
    });
};
/**
 * API Method to read a filie from the minio objectStorage
 * @param axios instance of axios
 * @param bucket to get the file from
 * @param path String of the folder structure and the filename e.g. customerId/orderId/filename.pdf
 * @param type contentType of the file
 * @returns found blob or undefined on error
 */
export const readFileFromMinio = async (
  axios: AxiosInstance,
  bucket: DocumentBucket,
  path: string,
  type: string,
): Promise<Blob | undefined> => {
  return axios
    .get('/storage/', {
      params: { bucket, path },
      responseType: 'arraybuffer',
    })
    .then((res) => new Blob([res.data], { type }))
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.readFile'),
      });
      console.error('Error while loading file', exc);
      return undefined;
    });
};

/**
 * API method to delete a file on the minio objectStorage and delete the SchambeckDoc for the file
 * @param axios instance of axios
 * @param request String of the folder structure and the filename e.g. customerId/orderId/filename.pdf
 * @returns boolean depending on success
 */
export const deleteFileFromMinio = async (
  axios: AxiosInstance,
  request: FileDeleteRequest,
): Promise<boolean> => {
  return axios
    .post('/storage/delete', request)
    .then((res) => res.status === 200)
    .catch((exc) => {
      generateNotification({
        type: NotificationType.ERROR,
        value: i18n.t('general.notification.error.deleteFile'),
      });
      console.error('Error while deleting file', exc);
      return false;
    });
};
/**
 * Util method to trigger download of a file
 * @param blob to download
 * @param filename of the download file
 */
export const downloadFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
